import { Link, useLocation } from 'react-router-dom';

import type { AdminUserSummary } from '../../types/admin';
import { resolvePortalHelpPath, resolvePortalProfilePath } from '../../config/portalHelpContent';
import { usePortalRefresh } from '../../contexts/PortalRefreshContext';
import NotificationBell from '../shared/NotificationBell';
import UserProfileAvatar from '../common/UserProfileAvatar';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';

interface AdminTopBarProps {
  user: AdminUserSummary;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onMenuToggle: () => void;
  onLogout: () => void;
  helpPath?: string;
}

export default function AdminTopBar({
  user,
  searchQuery,
  onSearchChange,
  onMenuToggle,
  onLogout,
  helpPath,
}: AdminTopBarProps) {
  const location = useLocation();
  const { refresh, hasRefresh } = usePortalRefresh();
  const resolvedHelpPath = helpPath ?? resolvePortalHelpPath(location.pathname);
  const resolvedProfilePath = resolvePortalProfilePath(location.pathname);
  const isHelpActive = resolvedHelpPath ? location.pathname.startsWith(resolvedHelpPath) : false;
  const isProfileActive = resolvedProfilePath
    ? location.pathname === resolvedProfilePath || location.pathname.startsWith(`${resolvedProfilePath}/`)
    : false;

  return (
    <header className="flex h-20 shrink-0 items-center justify-between bg-surface px-6 lg:px-10">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-white shadow-sm lg:hidden"
          onClick={onMenuToggle}
          aria-label="Ouvrir le menu"
        >
          <LucideSymbolIcon name="menu" tone="primary" size={22} />
        </button>
        <div className="group relative w-full max-w-md transition-transform duration-200 focus-within:scale-[1.02]">
          <span className="pointer-events-none absolute left-4 top-1/2 flex -translate-y-1/2">
            <LucideSymbolIcon name="search" tone="muted" size={20} />
          </span>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Rechercher un apprenant, une inscription…"
            className="w-full rounded-xl border-none bg-white py-3 pl-12 pr-4 text-sm shadow-sm placeholder:text-on-surface-variant/40 focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3 sm:gap-4 lg:gap-6">
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => {
              if (hasRefresh) refresh();
              else window.location.reload();
            }}
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-white shadow-sm transition-colors hover:bg-primary/5"
            aria-label="Actualiser la page"
            title="Actualiser"
          >
            <LucideSymbolIcon name="refresh" tone="muted" size={20} />
          </button>
          <NotificationBell iconTone="primary" />
          {resolvedHelpPath ? (
            <Link
              to={resolvedHelpPath}
              className={[
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm transition-colors hover:bg-primary/5',
                isHelpActive ? 'bg-primary/[0.08]' : '',
              ].join(' ')}
              aria-label="Centre d'aide"
              title="Centre d'aide"
            >
              <LucideSymbolIcon name="headset_mic" tone="primary" size={20} strokeWidth={2} />
            </Link>
          ) : null}
          {resolvedProfilePath ? (
            <Link
              to={resolvedProfilePath}
              className={[
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm transition-colors hover:bg-primary/5',
                isProfileActive ? 'bg-primary/[0.08]' : '',
              ].join(' ')}
              aria-label="Mon profil"
              title="Mon profil"
            >
              <LucideSymbolIcon name="person" tone="primary" size={20} />
            </Link>
          ) : null}
          <button
            type="button"
            onClick={onLogout}
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-white shadow-sm transition-colors hover:bg-red-50"
            aria-label="Déconnexion"
            title="Déconnexion"
          >
            <LucideSymbolIcon name="logout" tone="muted" size={20} />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden text-right sm:block">
            <p className="text-sm font-bold text-slate-900">{user.name}</p>
            <p className="text-xs text-on-surface-variant">{user.subtitle}</p>
          </div>
          <UserProfileAvatar
            name={user.name}
            prenom={user.prenom}
            nom={user.nom}
            photoUrl={user.photoUrl}
            avatarUrl={user.avatarUrl}
            size="sm"
          />
        </div>
      </div>
    </header>
  );
}
